/**
 * Where the API lives and how this client authenticates. The web app keeps every default (same
 * origin, refresh cookie). The Chrome extension panel (docs/extension.md §7) points it at an
 * absolute server URL, keeps its token in `chrome.storage.local` and has no refresh cookie at all.
 */
export interface TokenStore {
  load(): Promise<string | null>
  save(token: string | null): Promise<void>
}

export interface ClientInfo {
  kind: 'extension'
  version: string
  protocolVersion: number
}

export interface ApiConfig {
  /** Absolute server origin, e.g. `https://meow.example.com`. `''` = same origin (the web app). */
  baseUrl: string
  /** Recover a 401/4401 with the refresh cookie. The extension has none. */
  canRefresh: boolean
  tokenStore?: TokenStore
  /** Sent in the WS `auth` call so the server can mark the connection (docs/api.md §3.1). */
  client?: ClientInfo
}

const DEFAULTS: ApiConfig = { baseUrl: '', canRefresh: true }

let current: ApiConfig = DEFAULTS

export function configureApi(next: Partial<ApiConfig>): void {
  current = { ...current, ...next }
  if (next.baseUrl !== undefined) current.baseUrl = next.baseUrl.replace(/\/+$/, '')
}

export function apiConfig(): ApiConfig {
  return current
}

export function apiUrl(path: string): string {
  return current.baseUrl + path
}

/** `base` wins when set; otherwise the page's own origin (the web app). */
export function wsUrl(base: string, page: { protocol: string; host: string }): string {
  if (base) return `${base.replace(/^http/, 'ws')}/ws`
  return `${page.protocol === 'https:' ? 'wss' : 'ws'}://${page.host}/ws`
}

export function authParams(token: string, client?: ClientInfo): Record<string, unknown> {
  return client ? { token, client } : { token }
}
